/* global $ */
import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../components/Footer";

const SOURCES = [
    'Walk-in',
    'Phone',
    'WhatsApp',
    'Website',
    'Referral',
    'Social Media',
    'Email',
    'Google Ads',
    'Facebook Ads',
    'Instagram',
    'Partner / Agent',
    'Repeat Customer',
    'Other'
];

const TRAVEL_TYPES = [
    'Domestic',
    'International',
    'Honeymoon',
    'Family',
    'Corporate',
    'Adventure',
    'Pilgrimage',
    'Group Tour',
    'Luxury',
    'Budget',
    'Backpacking',
    'Visa Only',
    'Flight Only',
    'Hotel Only',
    'Other'
];

const STATUSES = [
    'new',                   // enquiry received
    'contacted',             // you sent package / replied
    'awaiting_response',     // no reply yet
    'follow_up_scheduled',   // customer said "later / 2 days"
    'follow_up_done',        // you followed up
    'interested',            // active discussion
    'qualified',             // serious buyer
    'negotiation',           // pricing / customization
    'not_responding',        // multiple follow-ups, no reply
    'lost',                  // not interested / rejected
    'converted'              // booking confirmed
];

function AddLead() {
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.state?.isEdit || false;
    const existingLead = location.state?.lead || null;

    const [isSaving, setIsSaving] = useState(false);
    const [loadingData, setLoadingData] = useState(isEdit);

    const [leadData, setLeadData] = useState({
        phone: '',
        alternate_phone: '',
        name: '',
        email: '',
        source: '',
        travel_type: '',
        preferred_start_date: '',
        no_of_people: '',
        country_id: '',
        state_id: '',
        city_id: '',
        notes: '',
        status: 'new',
        lead_queue_id: '',
        assigned_user_id: '',
    });

    const [queues, setQueues] = useState([]);
    const [users, setUsers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [allCities, setAllCities] = useState([]);
    const [filteredStates, setFilteredStates] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [errors, setErrors] = useState({});

    // ─── Load Dropdowns ───────────────────────────────────────────────────────────

    useEffect(() => {
        loadDropdowns();
    }, []);

    const loadDropdowns = async () => {
        const safe = async (url, setter, label) => {
            try {
                const res = await axiosInstance.get(url);
                const raw = res.data;
                const list = Array.isArray(raw) ? raw
                    : Array.isArray(raw?.data) ? raw.data
                        : Array.isArray(raw?.rows) ? raw.rows
                            : Array.isArray(raw?.result) ? raw.result
                                : [];
                console.log(`✅ ${label}: ${list.length} items`);
                setter(list);
            } catch (err) {
                console.error(`❌ ${label} FAILED:`, err?.response?.status, url);
                setter([]);
            }
        };

        await Promise.all([
            safe('/api/leadqueue', setQueues, 'LeadQueue'),
            safe('/api/users', setUsers, 'Users'),
            safe('/api/country', setCountries, 'Countries'),
            safe('/api/state', setAllStates, 'States'),
            safe('/api/cities', setAllCities, 'Cities'),
        ]);
    };

    // ─── Cascading: Country → State ───────────────────────────────────────────────

    useEffect(() => {
        if (leadData.country_id) {
            const filtered = allStates.filter(
                s => String(s.country_id) === String(leadData.country_id)
            );
            setFilteredStates(filtered);
            // Check if current state_id is still valid
            const stateStillValid = filtered.some(
                s => String(s.id) === String(leadData.state_id)
            );
            if (!stateStillValid) {
                setLeadData(prev => ({ ...prev, state_id: '', city_id: '' }));
                setFilteredCities([]);
            }
        } else {
            setFilteredStates([]);
            setFilteredCities([]);
        }
    }, [leadData.country_id, allStates]);

    // ─── Cascading: State → City ──────────────────────────────────────────────────

    useEffect(() => {
        if (leadData.state_id) {
            const filtered = allCities.filter(
                c => String(c.state_id) === String(leadData.state_id)
            );
            setFilteredCities(filtered);
            // Check if current city_id is still valid
            const cityStillValid = filtered.some(
                c => String(c.id) === String(leadData.city_id)
            );
            if (!cityStillValid) {
                setLeadData(prev => ({ ...prev, city_id: '' }));
            }
        } else {
            setFilteredCities([]);
        }
    }, [leadData.state_id, allCities]);

    // ─── Prefill Edit ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (isEdit && existingLead) {
            setLeadData({
                phone: existingLead.phone || '',
                alternate_phone: existingLead.alternate_phone || '',
                name: existingLead.name || '',
                email: existingLead.email || '',
                source: existingLead.source || '',
                travel_type: existingLead.travel_type || '',
                preferred_start_date: existingLead.preferred_start_date ? existingLead.preferred_start_date.split('T')[0] : '',
                no_of_people: existingLead.no_of_people || '',
                country_id: existingLead.country_id || '',
                state_id: existingLead.state_id || '',
                city_id: existingLead.city_id || '',
                notes: existingLead.notes || '',
                status: existingLead.status || 'new',
                lead_queue_id: existingLead.lead_queue_id || '',
                assigned_user_id: existingLead.assigned_user_id || '',
            });
            setLoadingData(false);
        }
    }, [isEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLeadData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    // ─── Validation ───────────────────────────────────────────────────────────────

    const validate = () => {
        const errs = {};
        if (!leadData.phone.trim()) errs.phone = 'Phone is required';
        if (leadData.phone && !/^[0-9]{10}$/.test(leadData.phone.trim())) errs.phone = 'Enter a valid 10-digit phone number';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ─── Save ─────────────────────────────────────────────────────────────────────

    const toNull = (v) => (v === '' || v === undefined ? null : v);

    const saveLead = async (e) => {
        e.preventDefault();
        if (isSaving) return;
        if (!validate()) return;
        setIsSaving(true);

        try {
            const user_id = localStorage.getItem('user_id');
            const payload = {
                ...leadData,
                name: leadData.name.trim() || `Lead-${leadData.phone}`,
                phone: leadData.phone.trim(),
                alternate_phone: toNull(leadData.alternate_phone),
                email: toNull(leadData.email),
                source: toNull(leadData.source),
                travel_type: toNull(leadData.travel_type),
                preferred_start_date: toNull(leadData.preferred_start_date),
                no_of_people: toNull(leadData.no_of_people),
                country_id: toNull(leadData.country_id),
                state_id: toNull(leadData.state_id),
                city_id: toNull(leadData.city_id),
                notes: toNull(leadData.notes),
                lead_queue_id: toNull(leadData.lead_queue_id),
                assigned_user_id: toNull(leadData.assigned_user_id),
                updated_by: user_id,
                isactive: true,
            };

            if (isEdit) {
                await axiosInstance.put(`/api/leads/${existingLead.id}`, payload);
                Swal.fire({ icon: 'success', title: 'Updated!', text: 'Lead updated successfully', timer: 1500, showConfirmButton: false });
                navigate(`/lead-details/${existingLead.id}`);
            } else {
                payload.created_by = user_id;
                const res = await axiosInstance.post('/api/leads', payload);
                const new_id = res.data?.data?.id || res.data?.id;
                Swal.fire({ icon: 'success', title: 'Saved!', text: 'Lead created successfully', timer: 1500, showConfirmButton: false });
                navigate(`/lead-details/${new_id}`);
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to save lead' });
        }
        setIsSaving(false);
    };

    if (loadingData) return (
        <div className="page-wrapper">
            <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            </div>
            <Footer />
        </div>
    );

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="card mb-0">
                        <div className="card-body">

                            <div className="page-header">
                                <div className="content-page-header d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <button type="button" className="btn btn-sm btn-outline-secondary me-3"
                                            onClick={() => isEdit ? navigate(`/lead-details/${existingLead.id}`) : navigate('/lead')}>
                                            <i className="fa fa-arrow-left me-1"></i> Back
                                        </button>
                                        <h5 className="mb-0">{isEdit ? 'Edit Lead' : 'Add Lead'}</h5>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={saveLead}>

                                {/* ── Contact Info ── */}
                                <h5 className="form-title">Contact Information</h5>
                                <div className="alert alert-info" style={{ fontSize: '13px' }}>
                                    <i className="fa fa-info-circle me-2"></i>
                                    Phone is the only required field. Name will be auto-set to <strong>Lead-[phone]</strong> if left blank.
                                </div>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Phone * <small className="text-muted">(10 digits)</small></label>
                                        <input type="text" className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                            name="phone" value={leadData.phone} onChange={handleChange} placeholder="9876543210" />
                                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Alternate Phone</label>
                                        <input type="text" className="form-control" name="alternate_phone"
                                            value={leadData.alternate_phone} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Name <small className="text-muted">(optional)</small></label>
                                        <input type="text" className="form-control" name="name"
                                            value={leadData.name} onChange={handleChange} placeholder="Leave blank for auto-name" />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Email</label>
                                        <input type="email" className="form-control" name="email"
                                            value={leadData.email} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Source</label>
                                        <select className="form-control" name="source" value={leadData.source} onChange={handleChange}>
                                            <option value="">Select Source</option>
                                            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Status</label>
                                        <select className="form-control" name="status" value={leadData.status} onChange={handleChange}>
                                            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Travel Details ── */}
                                <h5 className="form-title mt-3">Travel Details</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Travel Type</label>
                                        <select className="form-control" name="travel_type" value={leadData.travel_type} onChange={handleChange}>
                                            <option value="">Select Travel Type</option>
                                            {TRAVEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Preferred Start Date</label>
                                        <input type="date" className="form-control" name="preferred_start_date"
                                            value={leadData.preferred_start_date} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>No. of People</label>
                                        <input type="number" className="form-control" name="no_of_people"
                                            value={leadData.no_of_people} onChange={handleChange} min="1" />
                                    </div>
                                </div>

                                {/* ── Location ── */}
                                <h5 className="form-title mt-3">Location</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Country</label>
                                        <select className="form-control" name="country_id"
                                            value={leadData.country_id} onChange={handleChange}>
                                            <option value="">Select Country</option>
                                            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>State</label>
                                        <select className="form-control" name="state_id"
                                            value={leadData.state_id} onChange={handleChange}
                                            disabled={!leadData.country_id}>
                                            <option value="">
                                                {leadData.country_id ? 'Select State' : 'Select Country First'}
                                            </option>
                                            {filteredStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>City</label>
                                        <select className="form-control" name="city_id"
                                            value={leadData.city_id} onChange={handleChange}
                                            disabled={!leadData.state_id}>
                                            <option value="">
                                                {leadData.state_id ? 'Select City' : 'Select State First'}
                                            </option>
                                            {filteredCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Assignment ── */}
                                <h5 className="form-title mt-3">Assignment</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Lead Queue</label>
                                        <select className="form-control" name="lead_queue_id" value={leadData.lead_queue_id} onChange={handleChange}>
                                            <option value="">Select Queue</option>
                                            {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Assign To</label>
                                        <select className="form-control" name="assigned_user_id" value={leadData.assigned_user_id} onChange={handleChange}>
                                            <option value="">Unassigned</option>
                                            {users.map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Notes ── */}
                                <h5 className="form-title mt-3">Notes</h5>
                                <div className="row">
                                    <div className="col-md-12 mb-3">
                                        <textarea className="form-control" name="notes" rows="3"
                                            value={leadData.notes} onChange={handleChange}
                                            placeholder="Any additional notes about this lead..."></textarea>
                                    </div>
                                </div>

                                <div className="text-end mt-3">
                                    <button type="button" className="btn btn-secondary me-2"
                                        onClick={() => isEdit ? navigate(`/lead-details/${existingLead.id}`) : navigate('/lead')}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                        {isSaving ? (isEdit ? 'Updating...' : 'Saving...') : (isEdit ? 'Update Lead' : 'Save Lead')}
                                    </button>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}

export default AddLead;